import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, MessageCircle, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { trackConversion } from "@/lib/tracking";
import { useHeroIntro } from "@/hooks/useHeroIntro";

/**
 * Mobile-only sticky contact bar.
 *
 * - Renders only on viewports ≤768px (visually hidden via Tailwind's
 *   `min-[769px]:hidden`).
 * - "Bel ons" is identical everywhere; the secondary action is route-aware
 *   (see SECONDARY_CTA_CONFIG). The `?service=` query on /contact prefills
 *   the message field so the user lands with context.
 * - Every click is logged via trackConversion() with the context label so
 *   the admin dashboard can compare which surfaces convert.
 * - Auto-hides when the footer comes into view (or when the mobile menu
 *   is open) so it never sits on top of the user's destination.
 * - Honors `env(safe-area-inset-bottom)` for iPhone home-bar clearance.
 * - When mounted, reserves `--mobile-cta-h` of bottom padding on <main>
 *   so page content never sits behind the bar.
 */
const PHONE_HREF = "tel:+32476796175";

type SecondaryCtaContext =
  | "camp"
  | "kinderyoga"
  | "therapie"
  | "littlemetime"
  | "blog"
  | "default";

interface SecondaryCtaSpec {
  context: SecondaryCtaContext;
  to: string;
  label: string;
  icon: "sparkles" | "message";
  ariaLabel: string;
}

/**
 * Route → secondary CTA. Order matters: first matching prefix wins.
 * `/landing/*` and `/lp/*` are both mapped to the same context since the
 * router serves the paid-traffic landings at `/lp/*` while stakeholders
 * sometimes refer to them as `/landing/*`.
 */
const SECONDARY_CTA_CONFIG: Array<{
  test: (p: string) => boolean;
  spec: SecondaryCtaSpec;
}> = [
  {
    test: (p) => p.startsWith("/zomerkampen") || p.startsWith("/inschrijven"),
    spec: {
      context: "camp",
      to: "/inschrijven",
      label: "Inschrijven",
      icon: "sparkles",
      ariaLabel: "Schrijf je kind in voor het zomerkamp",
    },
  },
  {
    test: (p) =>
      p.startsWith("/kinderyoga") ||
      p.startsWith("/landing/kinderyoga") ||
      p.startsWith("/lp/kinderyoga"),
    spec: {
      context: "kinderyoga",
      to: "/contact?service=kinderyoga",
      label: "Yoga proberen",
      icon: "sparkles",
      ariaLabel: "Neem contact op over kinderyoga",
    },
  },
  {
    test: (p) =>
      p.startsWith("/sprankelpraktijk") ||
      p.startsWith("/landing/therapie") ||
      p.startsWith("/lp/therapie"),
    spec: {
      context: "therapie",
      to: "/contact?service=therapie",
      label: "Kennismaken",
      icon: "message",
      ariaLabel: "Neem contact op voor een kennismaking",
    },
  },
  {
    test: (p) => p.startsWith("/little-me-time"),
    spec: {
      context: "littlemetime",
      to: "/contact?service=littlemetime",
      label: "Meer weten",
      icon: "message",
      ariaLabel: "Meer weten over Little Me Time",
    },
  },
  {
    test: (p) => p.startsWith("/blog"),
    spec: {
      context: "blog",
      to: "/contact",
      label: "Contact",
      icon: "message",
      ariaLabel: "Ga naar de contactpagina",
    },
  },
];

const DEFAULT_SECONDARY: SecondaryCtaSpec = {
  context: "default",
  to: "/contact",
  label: "Contact",
  icon: "message",
  ariaLabel: "Ga naar de contactpagina",
};

const resolveSecondary = (pathname: string): SecondaryCtaSpec =>
  SECONDARY_CTA_CONFIG.find((c) => c.test(pathname))?.spec ?? DEFAULT_SECONDARY;

const readCookieVisible = () => {
  if (typeof document === "undefined") return false;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--cookie-consent-visible")
    .trim();
  return raw === "1";
};

const MobileStickyCta = () => {
  const [visible, setVisible] = useState(true);
  const [cookieBlocked, setCookieBlocked] = useState<boolean>(() => readCookieVisible());
  const [reappearDelay, setReappearDelay] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const secondary = resolveSecondary(pathname);
  const heroIntro = useHeroIntro();
  // Only delay the FIRST mount's entrance. Any subsequent show/hide cycle
  // (footer scroll, drawer close, cookie dismiss) uses the normal timing.
  const firstMountRef = useRef(true);
  useEffect(() => {
    firstMountRef.current = false;
  }, []);
  const introDelay = heroIntro && firstMountRef.current ? 1.1 : 0;

  // Watch :root style for --cookie-consent-visible flipping to "0" so we
  // can reveal the bar with a short delay (avoids abrupt appearance in
  // the same spot the banner just vacated).
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const update = () => {
      const next = readCookieVisible();
      setCookieBlocked((prev) => {
        if (prev && !next) setReappearDelay(0.2);
        else setReappearDelay(0);
        return next;
      });
    };
    update();
    const mo = new MutationObserver(update);
    mo.observe(root, { attributes: true, attributeFilter: ["style"] });
    return () => mo.disconnect();
  }, []);



  // Hide when footer (or any element marked data-hide-mobile-cta) is in view.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(max-width: 768px)").matches) return;

    const targets = document.querySelectorAll<HTMLElement>(
      "footer, [data-hide-mobile-cta]"
    );
    if (!targets.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        // If any sentinel is intersecting, hide the bar.
        const anyVisible = entries.some((e) => e.isIntersecting);
        setVisible(!anyVisible);
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.01 }
    );

    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, []);

  // Hide when the mobile drawer is open (drawer adds `inert` to <main>).
  useEffect(() => {
    if (typeof document === "undefined") return;
    const main = document.querySelector("main");
    if (!main) return;
    const mo = new MutationObserver(() => {
      if (main.hasAttribute("inert")) setVisible(false);
    });
    mo.observe(main, { attributes: true, attributeFilter: ["inert"] });
    return () => mo.disconnect();
  }, []);

  // Publish the bar's measured height as a CSS custom property so layout
  // can reserve space (we set bottom padding on <main> from Layout).
  useEffect(() => {
    if (cookieBlocked) {
      document.documentElement.style.setProperty("--mobile-cta-h", "0px");
      return;
    }
    const el = ref.current;
    if (!el) return;
    const publish = () => {
      const h = visible ? el.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty(
        "--mobile-cta-h",
        `${Math.round(h)}px`
      );
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener("resize", publish);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", publish);
      document.documentElement.style.removeProperty("--mobile-cta-h");
    };
  }, [visible, cookieBlocked]);

  return (
    <AnimatePresence>
      {visible && !cookieBlocked && (
        <motion.div
          ref={ref}
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.45, delay: Math.max(reappearDelay, introDelay), ease: [0.22, 0.68, 0.35, 1] }}
          className="fixed inset-x-0 bottom-0 z-[80] min-[769px]:hidden pointer-events-none"
          aria-label="Snel contact"
          role="navigation"
        >
          <div
            className="pointer-events-auto mx-auto w-full max-w-[640px] px-4"
            style={{
              paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)",
              paddingTop: "10px",
            }}
          >
            <div
              className="flex items-center gap-2.5 rounded-full border border-border/40 bg-background/92 px-2 py-2 backdrop-blur-xl"
              style={{
                boxShadow:
                  "0 1px 2px hsl(25 20% 18% / 0.06), 0 8px 28px -8px hsl(25 20% 18% / 0.18), 0 16px 48px -20px hsl(32 35% 52% / 0.10)",
              }}
            >
              <a
                href={PHONE_HREF}
                onClick={() =>
                  trackConversion("cta_click", {
                    label: `mobile_sticky_cta_${secondary.context}`,
                    action: "call",
                    path: pathname,
                  })
                }
                className="flex flex-1 min-h-[48px] items-center justify-center gap-2 rounded-full border border-border/50 bg-secondary/70 px-4 text-[14.5px] font-semibold text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label="Bel Praktijk Sprankelien op +32 476 79 61 75"
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
                <span>Bel ons</span>
              </a>
              <Link
                to={secondary.to}
                onClick={() =>
                  trackConversion("cta_click", {
                    label: `mobile_sticky_cta_${secondary.context}`,
                    action: "secondary",
                    path: pathname,
                    to: secondary.to,
                  })
                }
                className="flex flex-1 min-h-[48px] items-center justify-center gap-2 rounded-full bg-primary px-4 text-[14.5px] font-semibold text-primary-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label={secondary.ariaLabel}
              >
                {secondary.icon === "sparkles" ? (
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <MessageCircle className="h-4 w-4" aria-hidden="true" />
                )}
                <span>{secondary.label}</span>
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileStickyCta;
